import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import { toast } from "react-hot-toast";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { caps } from "../fmt.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { FormOption, FormOptions } from "../forms/FormTextField.tsx";
import { PremiumContentNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { ItemToBeRemovedWarning } from "./FormNote.tsx";
import { useVehicleContext } from "./ModelContext.tsx";
import { RichContent } from "./PremiumContent.tsx";
import { PreviewBanner } from "./StashPreviewBanner.tsx";
import { formOption } from "./style.css.ts";

export function ChooseModsDialog() {
  const vehicle = useVehicleContext();
  const defaultModIds = new Set(
    vehicle.defaultMods.map((m) => m.valueOrNull()?.id),
  );
  const dialog = useDialogContext();
  const canRemoveExisting = vehicle.can(EditMode.REMOVE_DIRECTLY, "mods");
  const { isCampaignMode } = vehicle.gang.mode;

  // Using vehicle.mods instead of modeRefs because we want the fully resolved
  // list of mod refs, not just the ones selected by the user.
  const initialModIds = vehicle.mods
    .filter((l) => l.isSuccess)
    .map((w) => w.value.id);

  const { form, submitName } = useForm({
    defaultValues: {
      modIds: initialModIds,
    },

    async onSubmit({ values }) {
      const modRefs = values.modIds
        .filter((id) => !defaultModIds.has(id))
        .map((id) => ({ id }));

      const nextVehicle = vehicle.addMods(modRefs);
      return await database.gangs.updateOne(nextVehicle.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });
  const modIds = useStoreState(form, (s) => s.values.modIds);
  const draftVehicle = vehicle.addMods(modIds.map((id) => ({ id })));

  return (
    <FormDialog
      overline="Krankshaft's"
      heading="Mod Installation Invoice"
      form={form}
    >
      <PreviewBanner draftGang={draftVehicle.gang} where="in the shop" />

      <PremiumContentNote />

      <FormOptions label="Vehicle Mods">
        {vehicle.ruleset.mods.toArray().map((mod) => {
          // In campaign mode, unselecting the mod will remove it without
          // refunding the player the caps cost.
          const willBeLost =
            isCampaignMode &&
            initialModIds.includes(mod.id) &&
            !modIds.includes(mod.id);

          // Default mods, and previously selected mods in gang modes that
          // do not allow direct removal should not allow unselection.
          const cannotUnselect =
            defaultModIds.has(mod.id) ||
            (!canRemoveExisting && initialModIds.includes(mod.id));

          return (
            <FormOption
              type="checkbox"
              key={mod.id}
              name={form.names.modIds}
              value={mod.id}
              info={caps(mod.cost)}
              label={mod.name}
              disabled={cannotUnselect}
              description={
                <>
                  <RichContent>{mod.description}</RichContent>
                  {willBeLost && (
                    <div className={formOption.extras}>
                      <ItemToBeRemovedWarning what="MOD" />
                    </div>
                  )}
                </>
              }
            />
          );
        })}
      </FormOptions>

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
